import { AdminTwoPanelLayout } from '@/components/admin/AdminTwoPanelLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import type { RouterOutput } from '@trapeze/server';
import { Edit, Phone, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_protected/admin/agent-transfer-destinations')({
  component: AgentTransferDestinationsPage,
});

type AgentWithDestinations = RouterOutput['admin']['getAgentsWithDestinations']['agents'][number];
type TransferDestination = AgentWithDestinations['destinations'][number];
type GlobalDefaults = RouterOutput['admin']['getAgentsWithDestinations']['globalDefaults'];

const STANDARD_DESTINATION_TYPES = [
  'front_desk',
  'technician',
  'medical_records',
  'surgical_coordinator',
  'contact_lens_team',
] as const;

function AgentTransferDestinationsPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const { organization } = useOrganization();
  const router = useRouter();

  const currentOrganizationId = organization?.id;

  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<AgentWithDestinations | null>(null);
  const [showDestinationDialog, setShowDestinationDialog] = useState(false);
  const [editingDestination, setEditingDestination] = useState<TransferDestination | null>(null);
  const [destinationType, setDestinationType] = useState('');
  const [customDestinationType, setCustomDestinationType] = useState('');
  const [transferType, setTransferType] = useState<'sip' | 'phone'>('sip');
  const [destination, setDestination] = useState('');
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    isOpen: boolean;
    destinationId: string;
    destinationType: string;
  }>({ isOpen: false, destinationId: '', destinationType: '' });

  const agentsQuery = trpc.admin.getAgentsWithDestinations.useQuery(
    {
      organizationId: currentOrganizationId || '',
      searchQuery: searchTerm,
    },
    { enabled: !!currentOrganizationId }
  );

  const createMutation = trpc.admin.createTransferDestination.useMutation({
    onSuccess: async () => {
      toast.success('Transfer destination created successfully');
      const result = await agentsQuery.refetch();
      // Update selectedAgent with fresh data
      if (selectedAgent && result.data?.agents) {
        const updatedAgent = result.data.agents.find((a) => a.id === selectedAgent.id);
        if (updatedAgent) setSelectedAgent(updatedAgent);
      }
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(`Failed to create destination: ${error.message}`);
    },
  });

  const updateMutation = trpc.admin.updateTransferDestination.useMutation({
    onSuccess: async () => {
      toast.success('Transfer destination updated successfully');
      const result = await agentsQuery.refetch();
      // Update selectedAgent with fresh data
      if (selectedAgent && result.data?.agents) {
        const updatedAgent = result.data.agents.find((a) => a.id === selectedAgent.id);
        if (updatedAgent) setSelectedAgent(updatedAgent);
      }
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(`Failed to update destination: ${error.message}`);
    },
  });

  const deleteMutation = trpc.admin.deleteTransferDestination.useMutation({
    onSuccess: async () => {
      toast.success('Transfer destination deleted successfully');
      const result = await agentsQuery.refetch();
      // Update selectedAgent with fresh data
      if (selectedAgent && result.data?.agents) {
        const updatedAgent = result.data.agents.find((a) => a.id === selectedAgent.id);
        if (updatedAgent) setSelectedAgent(updatedAgent);
      }
      setDeleteConfirmDialog({ isOpen: false, destinationId: '', destinationType: '' });
    },
    onError: (error) => {
      toast.error(`Failed to delete destination: ${error.message}`);
    },
  });

  const filteredAgents = agentsQuery.data?.agents || [];
  const globalDefaults: GlobalDefaults = agentsQuery.data?.globalDefaults || {};

  const handleSelectAgent = (agent: AgentWithDestinations) => {
    setSelectedAgent(agent);
  };

  const handleAddDestination = () => {
    if (!selectedAgent) return;
    setEditingDestination(null);
    setDestinationType('');
    setCustomDestinationType('');
    setTransferType('sip');
    setDestination('');
    setShowDestinationDialog(true);
  };

  const handleEditDestination = (dest: TransferDestination) => {
    setEditingDestination(dest);
    const isStandard = STANDARD_DESTINATION_TYPES.includes(
      dest.destinationType as (typeof STANDARD_DESTINATION_TYPES)[number]
    );
    if (isStandard) {
      setDestinationType(dest.destinationType);
      setCustomDestinationType('');
    } else {
      setDestinationType('custom');
      setCustomDestinationType(dest.destinationType);
    }
    setTransferType(dest.transferType as 'sip' | 'phone');
    setDestination(dest.destination);
    setShowDestinationDialog(true);
  };

  const handleDeleteDestination = (dest: TransferDestination) => {
    setDeleteConfirmDialog({
      isOpen: true,
      destinationId: dest.id,
      destinationType: dest.destinationType,
    });
  };

  const handleCloseDialog = () => {
    setShowDestinationDialog(false);
    setEditingDestination(null);
    setDestinationType('');
    setCustomDestinationType('');
    setTransferType('sip');
    setDestination('');
  };

  const handleSaveDestination = () => {
    if (!selectedAgent || !currentOrganizationId) return;

    const finalDestinationType =
      destinationType === 'custom' ? customDestinationType.trim() : destinationType;

    if (!finalDestinationType || !destination.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (editingDestination) {
      updateMutation.mutate({
        organizationId: currentOrganizationId,
        id: editingDestination.id,
        destinationType: finalDestinationType,
        transferType,
        destination: destination.trim(),
      });
    } else {
      createMutation.mutate({
        organizationId: currentOrganizationId,
        agentId: selectedAgent.id,
        destinationType: finalDestinationType,
        transferType,
        destination: destination.trim(),
      });
    }
  };

  const confirmDelete = () => {
    if (!currentOrganizationId) return;
    deleteMutation.mutate({
      organizationId: currentOrganizationId,
      id: deleteConfirmDialog.destinationId,
    });
  };

  const getGlobalDefault = (
    type: string
  ): { type: 'sip' | 'phone'; destination: string } | null => {
    return globalDefaults[type] || null;
  };

  if (isAdminLoading || !isSuperAdmin) {
    return null;
  }

  return (
    <AdminTwoPanelLayout
      title="Agent Transfer Destinations"
      description="Configure per-agent call transfer destinations that override global defaults"
      icon={Phone}
      isSuperAdminPage={true}
      sidebarWidth="equal"
      headerActions={
        selectedAgent && (
          <Button onClick={handleAddDestination}>
            <Plus className="h-4 w-4 mr-2" />
            Add Destination
          </Button>
        )
      }
      sidebarContent={
        <Card>
          <CardHeader>
            <CardTitle>Agents</CardTitle>
            <CardDescription>
              Select an agent to view and configure transfer destinations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by phone number or agent name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>

              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {agentsQuery.isLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading agents...</div>
                ) : filteredAgents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No agents found</div>
                ) : (
                  filteredAgents.map((agent) => (
                    <button
                      type="button"
                      key={agent.id}
                      onClick={() => handleSelectAgent(agent)}
                      className={`w-full text-left p-4 rounded-lg border transition-colors ${
                        selectedAgent?.id === agent.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="font-medium">
                              {agent.phoneIdentifier || 'No phone identifier'}
                            </div>
                            {agent.retellAgentId && (
                              <div className="text-sm text-muted-foreground">
                                {agent.retellAgentId}
                              </div>
                            )}
                          </div>
                        </div>
                        <Badge variant={agent.destinations.length > 0 ? 'default' : 'secondary'}>
                          {agent.destinations.length} configured
                        </Badge>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      }
      mainContent={
        <Card>
          <CardHeader>
            <CardTitle>Transfer Destinations</CardTitle>
            <CardDescription>
              {selectedAgent
                ? `Configure destinations for ${selectedAgent.phoneIdentifier || 'this agent'}`
                : 'Select an agent to view destinations'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedAgent ? (
              <div className="text-center py-12 text-muted-foreground">
                Select an agent from the list to view and configure transfer destinations
              </div>
            ) : (
              <div className="space-y-4">
                {selectedAgent.destinations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No custom destinations configured. This agent will use global defaults.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Destination Type</TableHead>
                        <TableHead>Transfer Type</TableHead>
                        <TableHead>Destination</TableHead>
                        <TableHead className="w-24">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedAgent.destinations.map((dest) => {
                        const isStandard = STANDARD_DESTINATION_TYPES.includes(
                          dest.destinationType as (typeof STANDARD_DESTINATION_TYPES)[number]
                        );
                        return (
                          <TableRow key={dest.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="capitalize">
                                  {dest.destinationType.replace(/_/g, ' ')}
                                </span>
                                {!isStandard && (
                                  <Badge variant="outline" className="text-xs">
                                    Custom
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{dest.transferType}</Badge>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{dest.destination}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditDestination(dest)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteDestination(dest)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}

                {/* Global Defaults Info */}
                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-3">Global Defaults (used as fallback)</h4>
                  <div className="space-y-2 text-sm">
                    {Object.entries(globalDefaults).map(([type, config]) => (
                      <div key={type} className="flex justify-between items-center">
                        <span className="text-muted-foreground">{type}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {config.type}
                          </Badge>
                          <span className="font-mono">{config.destination}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      }
    >
      {/* Add/Edit Destination Dialog */}
      <Dialog open={showDestinationDialog} onOpenChange={setShowDestinationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingDestination ? 'Edit Transfer Destination' : 'Add Transfer Destination'}
            </DialogTitle>
            <DialogDescription>
              Configure where calls should be transferred for this destination type
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Destination Type</Label>
              <Select value={destinationType} onValueChange={setDestinationType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select destination type" />
                </SelectTrigger>
                <SelectContent>
                  {STANDARD_DESTINATION_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom (enter below)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {destinationType === 'custom' && (
              <div className="space-y-2">
                <Label>Custom Destination Type</Label>
                <Input
                  placeholder="e.g., billing_team, scheduling"
                  value={customDestinationType}
                  onChange={(e) => setCustomDestinationType(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Transfer Type</Label>
              <Select
                value={transferType}
                onValueChange={(value) => setTransferType(value as 'sip' | 'phone')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sip">SIP (internal extension)</SelectItem>
                  <SelectItem value="phone">Phone (external number)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Destination</Label>
              <Input
                placeholder={
                  transferType === 'sip' ? 'e.g., sip:9718@domain.net' : 'e.g., +18773530295'
                }
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {transferType === 'sip'
                  ? 'Enter full SIP URI (e.g., sip:extension@domain.net)'
                  : 'Enter phone number with country code (e.g., +1234567890)'}
              </p>
            </div>

            {destinationType && destinationType !== 'custom' && (
              <div className="p-3 bg-muted/50 rounded-lg text-sm">
                <div className="font-medium mb-1">Global Default:</div>
                {(() => {
                  const globalDefault = getGlobalDefault(destinationType);
                  return globalDefault ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Badge variant="outline" className="text-xs">
                        {globalDefault.type}
                      </Badge>
                      <span className="font-mono">{globalDefault.destination}</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">No global default configured</span>
                  );
                })()}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveDestination}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingDestination ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.isOpen}
        onOpenChange={(isOpen) =>
          setDeleteConfirmDialog({ isOpen, destinationId: '', destinationType: '' })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Transfer Destination</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the destination for{' '}
              <span className="font-medium">{deleteConfirmDialog.destinationType}</span>? The agent
              will fall back to the global default for this destination type.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setDeleteConfirmDialog({ isOpen: false, destinationId: '', destinationType: '' })
              }
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminTwoPanelLayout>
  );
}
